/*
FuturisticSoundwave.jsx (updated)

- Exports a named `FuturisticSoundwave` React component (the visualizer) and a default export
  `FuturisticSoundwaveDemo` which is a small frontend console/demo page to test animations and
  interaction modes (hero/bar, live mic, manual sliders, presets).
- The demo includes: variant toggle, audioLevel slider, auto-simulate toggle, preset chooser,
  particle density slider, mic input toggle, and quick 'burst' buttons for testing particle/visual
  reactions.
- Drop this file into a React + Tailwind project. Default export is the demo wrapper so you can
  preview the component immediately.

Usage:
- Import the demo in your app root and render: `import FuturisticSoundwaveDemo from './FuturisticSoundwave';` then <FuturisticSoundwaveDemo />
- Or import the raw visualizer: `import { FuturisticSoundwave } from './FuturisticSoundwave';` and use it like: <FuturisticSoundwave audioLevel={0.5} variant="hero" />

Notes:
- The demo's mic mode uses Web Audio API to compute RMS from microphone input. Browsers will ask
  for permission when you enable "Use Microphone". If permission denied, it falls back to manual controls.
- This file keeps the original waveform engine but wires it to the demo controls.
*/

import React, { useEffect, useRef, useState } from 'react';

// -------------------------
// FuturisticSoundwave (visualizer)
// -------------------------
export function FuturisticSoundwave({
  audioLevel: externalLevel,
  speaking: externalSpeaking,
  variant = 'hero',
  width,
  height,
}) {
  // Defaults per variant
  const DEFAULTS = {
    hero: { w: 900, h: 280 },
    bar: { w: 1200, h: 120 },
  };
  const { w: defaultW, h: defaultH } = DEFAULTS[variant] || DEFAULTS.hero;
  const svgWidth = width || defaultW;
  const svgHeight = height || defaultH;

  const rafRef = useRef(null);
  const phaseRef = useRef(0);
  const [time, setTime] = useState(0);
  const [internalLevel, setInternalLevel] = useState(0);

  const audioLevel = typeof externalLevel === 'number' ? Math.max(0, Math.min(1, externalLevel)) : internalLevel;

  useEffect(() => {
    let t0 = performance.now();
    const step = (t) => {
      const dt = (t - t0) / 1000;
      t0 = t;
      phaseRef.current += dt * 2.0;
      setTime((s) => s + dt);

      if (typeof externalLevel !== 'number') {
        let simulated = 0;
        if (externalSpeaking) {
          // Enhanced speaking animation
          const base = 0.3 + 0.15 * Math.sin(time * 15);
          const burst = Math.max(0, Math.sin(time * 25) * 0.6 + 0.4);
          simulated = base + burst * 0.7;
        } else {
          // Quieter idle animation
          simulated = 0.02 + 0.015 * Math.sin(time * 0.6 + phaseRef.current * 0.5);
        }
        // Faster smoothing for more reactivity
        setInternalLevel((prev) => prev * 0.75 + simulated * 0.25);
      } else {
        setInternalLevel(0);
      }

      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [externalLevel, externalSpeaking, time]);

  function buildWave(amplitude = 1, frequency = 1.5, phase = 0, points = 120) {
    const w = svgWidth;
    const h = svgHeight;
    const midY = h / 2;
    const ampPx = (h / 2) * 0.85 * amplitude;
    let d = '';
    for (let i = 0; i <= points; i++) {
      const x = (i / points) * w;
      const t = (i / points) * Math.PI * 2;
      const y = midY +
        Math.sin(t * frequency + phase) * ampPx * (0.6 + 0.4 * Math.sin(t * 0.7 + phase * 0.3)) +
        0.16 * ampPx * Math.sin(t * frequency * 2.3 + phase * 1.9);
      d += `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)} `;
    }
    return d;
  }

  const phase = phaseRef.current;
  const layerParams = [
    { amp: 0.9, freq: 1.2, offset: 0.0 },
    { amp: 0.6, freq: 1.9, offset: 1.3 },
    { amp: 0.35, freq: 2.8, offset: 0.7 },
  ];

  const activeBoost = Math.max(0, (audioLevel - 0.02) / (1 - 0.02));
  const idleFactor = 0.3;

  const containerClasses = variant === 'bar' ? 'w-full py-4' : 'w-full flex items-center justify-center py-2';
  const ariaLabel = typeof externalLevel === 'number' ? `Audio level ${Math.round(externalLevel * 100)}%` : 'Soundwave visualization';

  return (
    <div className={`${containerClasses} select-none`}>
      <div className={variant === 'hero' ? 'max-w-4xl w-full mx-auto' : 'w-full'}>
        <div className="relative">
          <div className="absolute inset-0 -z-10 pointer-events-none flex items-center justify-center">
            <div className="w-full h-full blur-3xl opacity-30" style={{
              background: `radial-gradient(1200px 200px at 50% 50%, rgba(34, 211, 238, 0.2), transparent 25%, rgba(250, 204, 21, 0.1))`
            }} />
          </div>

          <svg width={svgWidth} height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="mx-auto block">
            <defs>
              <linearGradient id="g1" x1="0%" x2="100%" y1="0%" y2="0%">
                <stop offset="0%" stopColor="#22d3ee" /> 
                <stop offset="50%" stopColor="#ffffff" /> 
                <stop offset="100%" stopColor="#facc15" />
              </linearGradient>
              <linearGradient id="g2" x1="0%" x2="100%" y1="0%" y2="0%">
                <stop offset="0%" stopColor="#67e8f9" />
                <stop offset="50%" stopColor="#e2e8f0" />
                <stop offset="100%" stopColor="#fde047" />
              </linearGradient>
              <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="10" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <path d={buildWave(0.35 * (idleFactor + activeBoost * 0.25), 1.5, phase * 0.7)}
              stroke="url(#g2)" strokeOpacity={0.6} strokeWidth={2} fill="none" strokeLinecap="round" />

            {layerParams.map((lp, idx) => {
              const amp = lp.amp * (idleFactor + activeBoost * (0.7 + lp.amp * 0.5)) * (0.7 + 0.3 * Math.sin(time * (0.4 + idx * 0.15)));
              const d = buildWave(amp, lp.freq, phase + lp.offset, 160 + idx * 40);
              const widthStroke = 1.5 + (3 - idx);
              const opacity = 0.65 + (idx === 0 ? 0.35 * Math.min(1, audioLevel + 0.2) : 0.2);
              const gradient = idx === 0 ? 'url(#g1)' : 'url(#g2)';
              return (
                <path key={idx}
                  d={d}
                  stroke={gradient}
                  strokeWidth={widthStroke}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeOpacity={opacity}
                  fill="none"
                  style={{ filter: idx === 0 ? 'url(#glow)' : undefined, mixBlendMode: 'screen' }}
                />
              );
            })}

            <path d={buildWave(0.12 + activeBoost * 0.2, 1.6, phase * 1.3)} stroke="#ffffff" strokeOpacity={1} strokeWidth={1} fill="none" strokeLinecap="round" />

          </svg>

          <div className="absolute right-4 top-3 text-xs text-slate-400 opacity-80">
            <div aria-hidden>{`level: ${Math.round(audioLevel * 100)}%`}</div>
          </div>

          <div className="absolute w-full top-1/2 transform -translate-y-1/2 left-0 pointer-events-none">
            <div className="text-center text-2xl font-semibold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-slate-300 to-yellow-400 opacity-0 sm:opacity-100">
              {variant === 'hero' ? <span className="opacity-70">Voice — Visualization</span> : null}
            </div>
          </div>

        </div>
      </div>

      <style>{`
        @keyframes hueShift { 0%{filter:hue-rotate(0deg);} 50%{filter:hue-rotate(12deg);} 100%{filter:hue-rotate(0deg);} }
        svg{overflow:visible}
        svg path[stroke^='url(']{ will-change:stroke-dashoffset, opacity; animation:hueShift 8s ease-in-out infinite; }
      `}</style>

      <span className="sr-only" aria-live="polite">{ariaLabel}</span>
    </div>
  );
}

export default FuturisticSoundwave;